import { getSupabaseClient } from '@/integrations/supabase/client';
import type {
  Product,
  PromotionSelectionInput,
  ResolvedPromotionComponent,
  ProductIngredient,
  SelectedProductModifier,
} from '@/types';
import { stablePromoSelectionKey } from '@/lib/promotionSelectionKey';
import { generateUuid } from '@/lib/uuid';
import { composeKitchenNotes, lineUnitPrice, modifierSignature } from '@/lib/productModifiers';
import { productModifiersService } from './productModifiersService';
import { addModifierIngredientUsage, tabItemModifiersService } from './tabItemModifiersService';
import { promotionsService } from '@/services/promotions';
import { validateAndResolvePromotion } from '@/services/promotions/promotionValidation';
import { ingredientsService } from '@/services/ingredients/ingredientsService';
import { kitchenTicketsService } from '@/services/kitchen/kitchenTicketsService';
import type { CreateKitchenTicketItemInput } from '@/services/kitchen/kitchenTicketsService';
import { productsService } from './productsService';
import { productIngredientsService } from './productIngredientsService';
import { calculateWeightLinePrice, getPricePerKg, hasPositiveWeight, WEIGHT_EPS } from '@/utils/weightPricing';

const isKitchenProduct = (product: Product) => Boolean(product.productCategory?.printInKitchen);

export async function applyStockDelta(
  productId: string,
  recipe: ProductIngredient[],
  delta: number,
  isComposite?: boolean
): Promise<void> {
  const composite = isComposite ?? recipe.length > 0;
  if (composite) {
    if (recipe.length === 0) return;
    for (const row of recipe) {
      await ingredientsService.updateIngredientStockByDelta(
        row.ingredientId,
        delta * row.quantity
      );
    }
  } else {
    await productsService.updateProductStockByDelta(productId, delta);
  }
}

export async function applyPromotionComponentsStockDelta(
  components: Array<{ componentProductId: string; quantityPerBundle: number }>,
  bundleDelta: number
): Promise<void> {
  if (bundleDelta === 0 || components.length === 0) return;

  const productIds = [...new Set(components.map((c) => c.componentProductId))];
  const meta = await fetchProductMeta(productIds);

  for (const row of components) {
    const units = row.quantityPerBundle * bundleDelta;
    if (units === 0) continue;
    const isComposite = meta.get(row.componentProductId)?.isComposite ?? false;
    const recipe = isComposite
      ? await productIngredientsService.getByProduct(row.componentProductId)
      : [];
    await applyStockDelta(row.componentProductId, recipe, units, isComposite);
  }
}

async function fetchProductMeta(
  productIds: string[]
): Promise<Map<string, { name: string; isComposite: boolean }>> {
  if (productIds.length === 0) return new Map();
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('products')
    .select('id, name, is_composite')
    .in('id', [...new Set(productIds)]);
  if (error) throw error;
  const map = new Map<string, { name: string; isComposite: boolean }>();
  for (const row of data ?? []) {
    map.set(row.id as string, {
      name: row.name as string,
      isComposite: Boolean(row.is_composite),
    });
  }
  return map;
}

async function tabItemHasPromotionComponents(tabItemId: string): Promise<boolean> {
  const supabase = getSupabaseClient();
  const { count, error } = await supabase
    .from('tab_item_promotion_components')
    .select('id', { count: 'exact', head: true })
    .eq('tab_item_id', tabItemId);

  if (error) throw error;
  return (count ?? 0) > 0;
}

async function insertTabItemPromotionComponents(
  tabItemId: string,
  perBundle: ResolvedPromotionComponent[],
  nameMap: Map<string, string>
) {
  const supabase = getSupabaseClient();
  if (perBundle.length === 0) return;
  const rows = perBundle.map((row) => ({
    tab_item_id: tabItemId,
    component_product_id: row.componentProductId,
    quantity: row.quantity,
    product_name: nameMap.get(row.componentProductId) ?? 'Product',
    created_at: new Date().toISOString(),
  }));
  const { error } = await supabase.from('tab_item_promotion_components').insert(rows);
  if (error) throw error;
}

export const productInventoryService = {
  async addProductToTab({
    tabId,
    product,
    quantity = 1,
    promotionSelections,
    organizationId,
    clientItemId,
    createKitchenTickets,
    weightKg,
  }: {
    tabId: string;
    product: Product;
    quantity?: number;
    promotionSelections?: PromotionSelectionInput[];
    organizationId: string;
    clientItemId?: string;
    createKitchenTickets?: boolean;
    weightKg?: number;
  }) {
    const supabase = getSupabaseClient();
    const orgId = organizationId || product.organizationId;
    const hasWeight = hasPositiveWeight(weightKg);
    const lineQuantity = hasWeight ? 1 : quantity;
    const lineUnitPrice = hasWeight
      ? calculateWeightLinePrice(product.price, weightKg!)
      : product.price;
    const stockDelta = hasWeight ? weightKg! : quantity;

    const promotion =
      orgId != null
        ? await promotionsService.getActivePromotionByPromoProductId(orgId, product.id)
        : null;

    if (promotion) {
      const resolved = validateAndResolvePromotion(promotion, promotionSelections ?? []);

      const componentIds = resolved.map((r) => r.componentProductId);
      const productMeta = await fetchProductMeta(componentIds);
      const nameMap = new Map<string, string>(
        [...productMeta.entries()].map(([id, meta]) => [id, meta.name])
      );

      let isOutOfStock = false;
      for (const row of resolved) {
        if (productMeta.get(row.componentProductId)?.isComposite) continue;
        const stock = await productsService.getProductStock(row.componentProductId);
        const need = row.quantity * quantity;
        if (stock === 0 || stock < need) {
          isOutOfStock = true;
        }
      }

      const promoRow = {
        ...(clientItemId ? { id: clientItemId } : {}),
        tab_id: tabId,
        product_id: product.id,
        product_name: product.name,
        product_category: product.category,
        category_id: product.productCategory?.id ?? null,
        quantity,
        unit_price: product.price,
        discount: 0,
        notes: product.notes || null,
        created_at: new Date().toISOString(),
        organization_id: orgId,
      };

      const { data, error } = await supabase
        .from('tab_items')
        .upsert([promoRow], { onConflict: 'id', ignoreDuplicates: true })
        .select()
        .maybeSingle();

      if (error) throw error;

      const lineId = data?.id ?? clientItemId;
      if (data && lineId) {
        await insertTabItemPromotionComponents(lineId, resolved, nameMap);

        await applyPromotionComponentsStockDelta(
          resolved.map((row) => ({
            componentProductId: row.componentProductId,
            quantityPerBundle: row.quantity,
          })),
          -quantity
        );
      }

      const result = { ...(data ?? { ...promoRow, id: lineId }), isNewItem: true, isOutOfStock };

      if (createKitchenTickets && data && lineId && isKitchenProduct(product)) {
        await kitchenTicketsService.createTicketForTabItems(tabId, [
          {
            tabItemId: lineId,
            productId: product.id,
            productName: product.name,
            quantity,
            notes: product.notes || undefined,
            components: resolved.map((row) => ({
              componentProductId: row.componentProductId,
              productName: nameMap.get(row.componentProductId) ?? 'Product',
              quantity: row.quantity,
            })),
          },
        ]);
      }

      return result;
    }


    const isComposite = ((product as any).isComposite as boolean | undefined) ?? false;
    const recipe = isComposite
      ? await productIngredientsService.getByProduct(product.id)
      : [];

    let currentStock = 0;
    let isOutOfStock = false;
    if (!isComposite) {
      currentStock = await productsService.getProductStock(product.id);
      if (currentStock === 0) {
        isOutOfStock = true;
      }
    }

    const { data: existingItems } = await supabase
      .from('tab_items')
      .select('id, quantity, notes, product_id, weight_kg')
      .eq('tab_id', tabId)
      .eq('product_id', product.id);

    const mergeCandidates = hasWeight ? [] : (existingItems ?? []);
    let mergeTarget = mergeCandidates[0] ?? null;
    if (mergeTarget) {
      const hasPromo = await tabItemHasPromotionComponents(mergeTarget.id);
      if (hasPromo) mergeTarget = null;
      if (mergeTarget?.weight_kg != null) mergeTarget = null;
    }

    let tabItemResult;

    try {
      if (mergeTarget) {
        const { data, error } = await supabase
          .from('tab_items')
          .update({
            quantity: mergeTarget.quantity + lineQuantity,
          })
          .eq('id', mergeTarget.id)
          .select()
          .single();

        if (error) throw error;
        tabItemResult = { ...data, isNewItem: false, previousQuantity: mergeTarget.quantity };
      } else {
        const newRow = {
          ...(clientItemId ? { id: clientItemId } : {}),
          tab_id: tabId,
          product_id: product.id,
          product_name: product.name,
          product_category: product.category,
          category_id: product.productCategory?.id ?? null,
          quantity: lineQuantity,
          unit_price: lineUnitPrice,
          discount: 0,
          notes: product.notes || null,
          weight_kg: hasWeight ? weightKg : null,
          created_at: new Date().toISOString(),
          organization_id: orgId,
        };

        const { data, error } = await supabase
          .from('tab_items')
          .upsert([newRow], { onConflict: 'id', ignoreDuplicates: true })
          .select()
          .maybeSingle();

        if (error) throw error;
        tabItemResult = { ...(data ?? { ...newRow, id: clientItemId }), isNewItem: true };
      }

      if (isComposite) {
        await applyStockDelta(product.id, recipe, -stockDelta, true);
      } else if (currentStock > 0) {
        await productsService.updateProductStock(product.id, currentStock - stockDelta);
      }
    } catch (error) {
      console.error('Error adding product to tab:', error);
      throw error;
    }

    if (createKitchenTickets && tabItemResult && isKitchenProduct(product)) {
      await kitchenTicketsService.createTicketForTabItems(tabId, [
        {
          tabItemId: tabItemResult.id as string,
          productId: product.id,
          productName: product.name,
          quantity: lineQuantity,
          notes: product.notes || undefined,
        },
      ]);
    }

    return { ...tabItemResult, isOutOfStock };
  },

  async updateTabItemQuantity(
    itemId: string,
    newQuantity: number,
    createKitchenTickets?: boolean,
  ) {
    const supabase = getSupabaseClient();

    try {
      const { data: currentItem, error: itemError } = await supabase
        .from('tab_items')
        .select('product_id, quantity, weight_kg, created_at, product_name, tab_id, notes, category_id, products(is_composite)')
        .eq('id', itemId)
        .single();

      if (itemError) throw itemError;

      let printInKitchen = false;
      if (currentItem.category_id) {
        const { data: category, error: categoryError } = await supabase
          .from('product_categories')
          .select('print_in_kitchen')
          .eq('id', currentItem.category_id)
          .single();
        if (categoryError) throw categoryError;
        printInKitchen = (category?.print_in_kitchen as boolean) ?? false;
      }

      const { data: promoRows, error: promoErr } = await supabase
        .from('tab_item_promotion_components')
        .select('component_product_id, quantity, product_name')
        .eq('tab_item_id', itemId);

      if (promoErr) throw promoErr;
      const isPromoLine = (promoRows?.length ?? 0) > 0;
      const weightKg =
        currentItem.weight_kg != null ? Number(currentItem.weight_kg) : null;
      const isWeight = hasPositiveWeight(weightKg);

      // Weight lines stay quantity=1; only full remove is supported.
      if (isWeight && newQuantity > 0 && newQuantity !== currentItem.quantity) {
        return currentItem;
      }

      const lineModifiers = (await tabItemModifiersService.getByTabItemIds([itemId])).get(itemId) ?? [];
      const lineModifierIds = lineModifiers.map((modifier) => modifier.modifierId);

      const quantityChange = currentItem.quantity - newQuantity;
      const isComposite = (currentItem.products as { is_composite?: boolean } | null)?.is_composite ?? false;
      const recipe = isComposite
        ? await productIngredientsService.getByProduct(currentItem.product_id)
        : [];

      const promoComponents = (promoRows ?? []).map((row) => ({
        componentProductId: row.component_product_id as string,
        quantityPerBundle: Number(row.quantity),
        productName: (row.product_name as string) ?? 'Product',
      }));

      if (newQuantity <= 0) {
        if (isPromoLine) {
          await applyPromotionComponentsStockDelta(promoComponents, Number(currentItem.quantity));
          const { error: delComp } = await supabase
            .from('tab_item_promotion_components')
            .delete()
            .eq('tab_item_id', itemId);
          if (delComp) throw delComp;
        } else {
          await applyStockDelta(
            currentItem.product_id,
            recipe,
            isWeight ? weightKg! : currentItem.quantity,
            isComposite
          );
          await tabItemModifiersService.applyStockDelta(lineModifierIds, Number(currentItem.quantity));
        }

        // Cancel kitchen lines before deleting tab_item (FK is ON DELETE SET NULL).
        if (createKitchenTickets && printInKitchen) {
          await kitchenTicketsService.cancelTicketItemsForTabItem(
            itemId,
            Number(currentItem.quantity),
          );
        }

        const { error } = await supabase
          .from('tab_items')
          .delete()
          .eq('id', itemId);

        if (error) throw error;
        return null;
      }

      const { data, error } = await supabase
        .from('tab_items')
        .update({
          quantity: newQuantity,
        })
        .eq('id', itemId)
        .select()
        .single();

      if (error) throw error;

      if (isPromoLine) {
        await applyPromotionComponentsStockDelta(promoComponents, quantityChange);
      } else if (quantityChange !== 0) {
        await applyStockDelta(currentItem.product_id, recipe, quantityChange, isComposite);
        await tabItemModifiersService.applyStockDelta(lineModifierIds, quantityChange);
      }

      const addedQuantity = newQuantity - currentItem.quantity;

      if (createKitchenTickets && addedQuantity > 0 && printInKitchen) {
        await kitchenTicketsService.createTicketForTabItems(currentItem.tab_id as string, [
          {
            tabItemId: itemId,
            productId: currentItem.product_id as string,
            productName: currentItem.product_name as string,
            quantity: addedQuantity,
            notes: composeKitchenNotes(currentItem.notes as string | null, lineModifiers),
            components: isPromoLine
              ? promoComponents.map((comp) => ({
                  componentProductId: comp.componentProductId,
                  productName: comp.productName,
                  quantity: comp.quantityPerBundle,
                }))
              : undefined,
          },
        ]);
      } else if (createKitchenTickets && addedQuantity < 0 && printInKitchen) {
        await kitchenTicketsService.cancelTicketItemsForTabItem(itemId, -addedQuantity);
      }

      return data;
    } catch (error) {
      console.error('Error updating tab item quantity:', error);
      throw error;
    }
  },

  async updateTabItemWeight(
    itemId: string,
    newWeightKg: number,
    createKitchenTickets?: boolean,
  ) {
    const supabase = getSupabaseClient();

    try {
      const { data: currentItem, error: itemError } = await supabase
        .from('tab_items')
        .select(
          'product_id, quantity, weight_kg, unit_price, created_at, product_name, tab_id, notes, category_id, products(is_composite, price)'
        )
        .eq('id', itemId)
        .single();

      if (itemError) throw itemError;

      const prevWeight =
        currentItem.weight_kg != null ? Number(currentItem.weight_kg) : null;
      if (prevWeight == null || prevWeight <= 0) {
        throw new Error('Item is not a sold-by-weight line');
      }

      let printInKitchen = false;
      if (currentItem.category_id) {
        const { data: category, error: categoryError } = await supabase
          .from('product_categories')
          .select('print_in_kitchen')
          .eq('id', currentItem.category_id)
          .single();
        if (categoryError) throw categoryError;
        printInKitchen = (category?.print_in_kitchen as boolean) ?? false;
      }

      const productMeta = currentItem.products as
        | { is_composite?: boolean; price?: number }
        | null;
      const isComposite = productMeta?.is_composite ?? false;
      const recipe = isComposite
        ? await productIngredientsService.getByProduct(currentItem.product_id)
        : [];
      const pricePerKg = getPricePerKg({
        unitPrice: Number(currentItem.unit_price),
        weightKg: prevWeight,
        catalogPrice: productMeta?.price,
      });

      if (newWeightKg <= 0) {
        await applyStockDelta(currentItem.product_id, recipe, prevWeight, isComposite);

        if (createKitchenTickets && printInKitchen) {
          await kitchenTicketsService.cancelTicketItemsForTabItem(
            itemId,
            Number(currentItem.quantity),
          );
        }

        const { error } = await supabase.from('tab_items').delete().eq('id', itemId);
        if (error) throw error;
        return null;
      }

      if (Math.abs(newWeightKg - prevWeight) < WEIGHT_EPS) {
        return currentItem;
      }

      const newUnitPrice = calculateWeightLinePrice(pricePerKg, newWeightKg);
      const weightChange = prevWeight - newWeightKg;

      const { data, error } = await supabase
        .from('tab_items')
        .update({
          weight_kg: newWeightKg,
          unit_price: newUnitPrice,
          quantity: 1,
        })
        .eq('id', itemId)
        .select()
        .single();

      if (error) throw error;

      if (weightChange !== 0) {
        await applyStockDelta(currentItem.product_id, recipe, weightChange, isComposite);
      }

      return data;
    } catch (error) {
      console.error('Error updating tab item weight:', error);
      throw error;
    }
  },

  async updateTabItemDiscount(itemId: string, discount: number) {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase.from('tab_items').update({ discount }).eq('id', itemId).select().single();

    if (error) throw error;
    return data;
  },

  async updateTabItemNotes(itemId: string, notes: string | null) {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
      .from('tab_items')
      .update({ notes: notes.trim() || null })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async addProductsToTabBulk({
    tabId,
    products,
    organizationId,
    createKitchenTickets,
  }: {
    tabId: string;
    /** `clientItemId` (when provided) is used as the id of newly inserted tab_items so offline-created items are referenceable and replays are idempotent. */
    products: Array<{
      product: Product;
      quantity: number;
      notes?: string;
      customerPosition?: number;
      promotionSelections?: PromotionSelectionInput[];
      /** Ignored on promo lines. */
      modifiers?: SelectedProductModifier[];
      clientItemId?: string;
      weightKg?: number;
    }>;
    organizationId: string;
    createKitchenTickets?: boolean;
  }) {
    const supabase = getSupabaseClient();

    if (!products.length) {
      return [];
    }

    const normalizeNotes = (notes?: string) => notes?.trim() || null;
    const normalizeCustomerPosition = (value?: number | null) =>
      typeof value === 'number' && value > 0 ? value : null;
    const lineGroupKey = (
      productId: string,
      notes: string | null,
      customerPosition: number | null,
      suffix = '',
    ) => `${productId}:${notes || ''}:${customerPosition ?? ''}${suffix}`;
    const modifiersSuffix = (signature: string) => (signature ? `:mods:${signature}` : '');

    // Group products by productId + notes + modifiers (any difference = separate items)
    const orgId = organizationId || products[0]?.product.organizationId;
    const promoProductIdSet = new Set<string>();
    if (orgId) {
      const { data: promoDefs, error: promoListErr } = await supabase
        .from('product_promotions')
        .select('promo_product_id')
        .eq('organization_id', orgId)
        .eq('is_active', true);
      if (promoListErr) throw promoListErr;
      for (const row of promoDefs ?? []) {
        promoProductIdSet.add(row.promo_product_id as string);
      }
    }

    const groupedProducts = new Map<
      string,
      {
        key: string;
        product: Product;
        quantity: number;
        notes: string | null;
        customerPosition: number | null;
        promotionSelections?: PromotionSelectionInput[];
        modifiers: SelectedProductModifier[];
        clientItemId?: string;
        weightKg?: number;
        originalEntries: Array<{
          product: Product;
          quantity: number;
          notes?: string;
          customerPosition?: number;
          promotionSelections?: PromotionSelectionInput[];
          modifiers?: SelectedProductModifier[];
          clientItemId?: string;
          weightKg?: number;
        }>;
      }
    >();

    for (const entry of products) {
      const normalizedNotes = normalizeNotes(entry.notes);
      const normalizedPosition = normalizeCustomerPosition(entry.customerPosition);
      const isPromoProduct = promoProductIdSet.has(entry.product.id);
      const hasWeight = hasPositiveWeight(entry.weightKg);
      // Weight lines are priced per kg, so structured modifiers never apply to them.
      const modifiers = isPromoProduct || hasWeight ? [] : entry.modifiers ?? [];
      // Each weigh-in is its own line — never merge.
      const key = hasWeight
        ? lineGroupKey(
            entry.product.id,
            normalizedNotes,
            normalizedPosition,
            `:weight:${entry.clientItemId ?? crypto.randomUUID()}`,
          )
        : isPromoProduct
          ? lineGroupKey(
              entry.product.id,
              normalizedNotes,
              normalizedPosition,
              `:promo:${stablePromoSelectionKey(entry.promotionSelections)}`,
            )
          : lineGroupKey(
              entry.product.id,
              normalizedNotes,
              normalizedPosition,
              modifiersSuffix(modifierSignature(modifiers)),
            );

      if (groupedProducts.has(key) && !hasWeight) {
        const existing = groupedProducts.get(key)!;
        existing.quantity += entry.quantity;
        existing.originalEntries.push(entry);
      } else {
        groupedProducts.set(key, {
          key,
          product: entry.product,
          quantity: hasWeight ? 1 : entry.quantity,
          notes: normalizedNotes,
          customerPosition: normalizedPosition,
          promotionSelections: entry.promotionSelections,
          modifiers,
          clientItemId: entry.clientItemId,
          weightKg: hasWeight ? entry.weightKg : undefined,
          originalEntries: [entry],
        });
      }
    }

    const groupedArray = Array.from(groupedProducts.values());

    const promoEntries = groupedArray.filter((g) => promoProductIdSet.has(g.product.id));
    const regularEntries = groupedArray.filter((g) => !promoProductIdSet.has(g.product.id));

    const promoResults: Array<{
      tabItem: Record<string, unknown> | null;
      isNewItem: boolean;
      isOutOfStock: boolean;
      previousQuantity?: number;
    }> = [];

    // Collect kitchen items (promos + regular) to create one ticket at the end.
    const kitchenTicketItems: CreateKitchenTicketItemInput[] = [];

    // Batch promo tab_items into one INSERT so kitchen tickets can be created
    // as a single ticket for the whole "add to tab" action (instead of one per promo).
    if (promoEntries.length > 0) {
      type PreparedPromo = {
        promoRow: {
          id: string;
          tab_id: string;
          product_id: string;
          product_name: string;
          product_category: string;
          category_id: string | null;
          quantity: number;
          unit_price: number;
          discount: number;
          notes: string | null;
          customer_position: number | null;
          created_at: string;
          organization_id: string;
        };
        product: Product;
        resolved: ResolvedPromotionComponent[];
        nameMap: Map<string, string>;
        isOutOfStock: boolean;
      };

      const preparedPromos: PreparedPromo[] = [];

      for (const grouped of promoEntries) {
        const promotion = orgId
          ? await promotionsService.getActivePromotionByPromoProductId(orgId, grouped.product.id)
          : null;
        if (!promotion) {
          // Fall back to the single-item path if the promo definition disappeared.
          // It will create its own kitchen ticket if the feature is on.
          const res = await productInventoryService.addProductToTab({
            tabId,
            product: grouped.product,
            quantity: grouped.quantity,
            promotionSelections: grouped.promotionSelections,
            organizationId,
            clientItemId: grouped.clientItemId,
            createKitchenTickets,
          });
          promoResults.push({
            tabItem: res as unknown as Record<string, unknown>,
            isNewItem: Boolean((res as { isNewItem?: boolean }).isNewItem),
            isOutOfStock: Boolean((res as { isOutOfStock?: boolean }).isOutOfStock),
            previousQuantity: (res as { previousQuantity?: number }).previousQuantity,
          });
          continue;
        }

        const resolved = validateAndResolvePromotion(promotion, grouped.promotionSelections ?? []);
        const componentIds = resolved.map((r) => r.componentProductId);
        const productMeta = await fetchProductMeta(componentIds);
        const nameMap = new Map<string, string>(
          [...productMeta.entries()].map(([id, meta]) => [id, meta.name])
        );

        let isOutOfStock = false;
        for (const row of resolved) {
          if (productMeta.get(row.componentProductId)?.isComposite) continue;
          const stock = await productsService.getProductStock(row.componentProductId);
          const need = row.quantity * grouped.quantity;
          if (stock === 0 || stock < need) {
            isOutOfStock = true;
          }
        }

        preparedPromos.push({
          promoRow: {
            id: grouped.clientItemId ?? crypto.randomUUID(),
            tab_id: tabId,
            product_id: grouped.product.id,
            product_name: grouped.product.name,
            product_category: grouped.product.category,
            category_id: grouped.product.productCategory?.id ?? null,
            quantity: grouped.quantity,
            unit_price: grouped.product.price,
            discount: 0,
            notes: grouped.notes,
            customer_position: grouped.customerPosition,
            created_at: new Date().toISOString(),
            organization_id: orgId ?? organizationId,
          },
          product: grouped.product,
          resolved,
          nameMap,
          isOutOfStock,
        });
      }

      if (preparedPromos.length > 0) {
        const { data: insertedPromos, error: promoInsertError } = await supabase
          .from('tab_items')
          .upsert(
            preparedPromos.map((p) => p.promoRow),
            { onConflict: 'id', ignoreDuplicates: true }
          )
          .select();

        if (promoInsertError) throw promoInsertError;

        const insertedById = new Map((insertedPromos ?? []).map((row) => [row.id as string, row]));

        for (const prepared of preparedPromos) {
          const lineId = prepared.promoRow.id;
          const insertedRow = insertedById.get(lineId) ?? null;

          // ignoreDuplicates may return no row on replay; skip side effects then.
          if (insertedRow) {
            await insertTabItemPromotionComponents(lineId, prepared.resolved, prepared.nameMap);
            await applyPromotionComponentsStockDelta(
              prepared.resolved.map((row) => ({
                componentProductId: row.componentProductId,
                quantityPerBundle: row.quantity,
              })),
              -prepared.promoRow.quantity
            );

            if (createKitchenTickets && isKitchenProduct(prepared.product)) {
              kitchenTicketItems.push({
                tabItemId: lineId,
                productId: prepared.product.id,
                productName: prepared.product.name,
                quantity: prepared.promoRow.quantity,
                notes: prepared.promoRow.notes || undefined,
                components: prepared.resolved.map((row) => ({
                  componentProductId: row.componentProductId,
                  productName: prepared.nameMap.get(row.componentProductId) ?? 'Product',
                  quantity: row.quantity,
                })),
              });
            }
          }

          promoResults.push({
            tabItem: (insertedRow as Record<string, unknown> | null) ?? {
              ...prepared.promoRow,
              id: lineId,
            },
            isNewItem: true,
            isOutOfStock: prepared.isOutOfStock,
          });
        }
      }
    }

    if (regularEntries.length === 0) {
      return promoResults;
    }

    const uniqueProductIds = Array.from(new Set(regularEntries.map((g) => g.product.id)));

    const stockChecks = await Promise.all(
      uniqueProductIds.map((productId) =>
        productsService.getProductStock(productId).then((stock) => ({ productId, stock }))
      )
    );
    const stockMap = new Map(stockChecks.map(({ productId, stock }) => [productId, stock]));

    const recipeMap = new Map<string, ProductIngredient[]>();
    const compositeProductIds = regularEntries
      .filter((g) => ((g.product as any).isComposite as boolean | undefined) ?? false)
      .map((g) => g.product.id);
    const uniqueCompositeIds = Array.from(new Set(compositeProductIds));
    await Promise.all(
      uniqueCompositeIds.map(async (productId) => {
        const recipe = await productIngredientsService.getByProduct(productId);
        recipeMap.set(productId, recipe);
      })
    );

    const { data: existingItems, error: existingItemsError } = await supabase
      .from('tab_items')
      .select('*')
      .eq('tab_id', tabId)
      .in('product_id', uniqueProductIds);

    if (existingItemsError) throw existingItemsError;

    const existingItemsMap = new Map<string, (typeof existingItems)[0]>();
    if (existingItems?.length) {
      const tabItemIds = existingItems.map((item) => item.id);
      const { data: compRows } = await supabase
        .from('tab_item_promotion_components')
        .select('tab_item_id')
        .in('tab_item_id', tabItemIds);
      const promoTabItemIds = new Set((compRows ?? []).map((r: { tab_item_id: string }) => r.tab_item_id));
      const existingModifiers = await tabItemModifiersService.getByTabItemIds(tabItemIds);

      for (const item of existingItems) {
        if (item.weight_kg != null) continue;
        const key = lineGroupKey(
          item.product_id as string,
          normalizeNotes(item.notes),
          normalizeCustomerPosition(item.customer_position as number | null),
          modifiersSuffix(modifierSignature(existingModifiers.get(item.id))),
        );
        if (!promoTabItemIds.has(item.id)) {
          existingItemsMap.set(key, item);
        }
      }
    }

    const stagedModifierIds = [
      ...new Set(regularEntries.flatMap((g) => g.modifiers.map((modifier) => modifier.modifierId))),
    ];
    const [modifierRecipes, availableModifierIds] = await Promise.all([
      productModifiersService.getIngredientsByModifierIds(stagedModifierIds),
      productModifiersService.getExistingModifierIds(stagedModifierIds),
    ]);
    // A modifier deleted after it was staged keeps its snapshot but loses the reference.
    const toModifierRows = (modifiers: SelectedProductModifier[]) =>
      modifiers.map((modifier) => ({
        ...modifier,
        modifierId: availableModifierIds.has(modifier.modifierId) ? modifier.modifierId : null,
      }));
    const newItemModifiers: Array<{ tabItemId: string; modifiers: SelectedProductModifier[] }> = [];

    const regularResults: Array<{
      tabItem: {
        id: string;
        tab_id: string;
        product_id: string;
        product_name: string;
        product_category: string;
        category_id: string | null;
        quantity: number;
        unit_price: number;
        discount: number;
        notes: string | null;
        customer_position: number | null;
        created_at: string;
      } | null;
      isNewItem: boolean;
      isOutOfStock: boolean;
      previousQuantity?: number;
    }> = [];

    const resultIndexMap = new Map<string, number>();
    const insertedResultIndexById = new Map<string, number>();

    const itemsToInsert: Array<{
      id: string;
      tab_id: string;
      product_id: string;
      product_name: string;
      product_category: string;
      category_id: string | null;
      quantity: number;
      unit_price: number;
      discount: number;
      notes: string | null;
      customer_position: number | null;
      weight_kg: number | null;
      created_at: string;
      organization_id: string;
    }> = [];

    const itemsToUpdate: Array<{
      id: string;
      quantity: number;
      key: string;
    }> = [];

    //amount of units to subtract from each simple product's stock
    const simpleDeductions = new Map<string, number>();
    // ingredient units to subtract: composite product recipes + modifier recipes
    const ingredientDeductions = new Map<string, number>();

    for (const grouped of regularEntries) {
      const { key, product, quantity, notes, customerPosition, modifiers, weightKg } = grouped;
      const hasWeight = hasPositiveWeight(weightKg);
      // Weight lines never merge with existing items.
      const existingItem = hasWeight ? undefined : existingItemsMap.get(key);
      const recipe = recipeMap.get(product.id) ?? [];
      const isComposite = ((product as any).isComposite as boolean | undefined) ?? false;
      const currentStock = stockMap.get(product.id) ?? 0;
      const isOutOfStock = !isComposite && currentStock === 0;
      const lineQuantity = hasWeight ? 1 : quantity;
      const linePrice = hasWeight
        ? calculateWeightLinePrice(product.price, weightKg!)
        : lineUnitPrice(product, modifiers);
      const stockUnits = hasWeight ? weightKg! : quantity;

      if (existingItem) {
        const newQuantity = existingItem.quantity + lineQuantity;
        itemsToUpdate.push({
          id: existingItem.id,
          quantity: newQuantity,
          key,
        });

        const resultIndex = regularResults.length;
        resultIndexMap.set(key, resultIndex);
        regularResults.push({
          tabItem: { ...existingItem, quantity: newQuantity },
          isNewItem: false,
          isOutOfStock,
          previousQuantity: existingItem.quantity,
        });
      } else {
        const itemId = grouped.clientItemId ?? generateUuid();
        itemsToInsert.push({
          id: itemId,
          tab_id: tabId,
          product_id: product.id,
          product_name: product.name,
          product_category: product.category,
          category_id: product.productCategory?.id || null,
          quantity: lineQuantity,
          unit_price: linePrice,
          discount: 0,
          notes,
          customer_position: customerPosition,
          weight_kg: hasWeight ? weightKg! : null,
          created_at: new Date().toISOString(),
          organization_id: orgId ?? organizationId,
        });
        if (modifiers.length > 0) {
          newItemModifiers.push({ tabItemId: itemId, modifiers: toModifierRows(modifiers) });
        }

        const resultIndex = regularResults.length;
        resultIndexMap.set(key, resultIndex);
        insertedResultIndexById.set(itemId, resultIndex);
        regularResults.push({
          tabItem: null,
          isNewItem: true,
          isOutOfStock,
        });
      }

      if (isComposite) {
        for (const ingredient of recipe) {
          const add = ingredient.quantity * stockUnits;
          ingredientDeductions.set(
            ingredient.ingredientId,
            (ingredientDeductions.get(ingredient.ingredientId) ?? 0) + add
          );
        }
      } else if (currentStock > 0) {
        simpleDeductions.set(
          product.id,
          (simpleDeductions.get(product.id) ?? 0) + stockUnits
        );
      }
      addModifierIngredientUsage(
        ingredientDeductions,
        modifierRecipes,
        modifiers.map((modifier) => modifier.modifierId),
        quantity
      );
    }

    try {
      // Perform batch inserts. Upsert with ignoreDuplicates makes a replayed
      // offline mutation (client-generated ids) a no-op instead of a 23505 error.
      if (itemsToInsert.length > 0) {
        const { data: insertedItems, error: insertError } = await supabase
          .from('tab_items')
          .upsert(itemsToInsert, { onConflict: 'id', ignoreDuplicates: true })
          .select();

        if (insertError) throw insertError;

        for (const item of insertedItems ?? []) {
          const resultIndex = insertedResultIndexById.get(item.id as string);
          if (resultIndex !== undefined) {
            regularResults[resultIndex].tabItem = item;
          }
        }
      }

      // Upserted even when the line already existed (replay after a partial failure).
      for (const { tabItemId, modifiers } of newItemModifiers) {
        await tabItemModifiersService.insertForTabItem(tabItemId, orgId ?? organizationId, modifiers);
      }

      if (itemsToUpdate.length > 0) {
        const updatePromises = itemsToUpdate.map(async ({ id, quantity, key }) => {
          const { data, error } = await supabase
            .from('tab_items')
            .update({ quantity })
            .eq('id', id)
            .select()
            .single();

          if (error) throw error;
          return { data, key };
        });

        const updatedItems = await Promise.all(updatePromises);

        for (const { data: updatedItem, key } of updatedItems) {
          const resultIndex = resultIndexMap.get(key);
          if (resultIndex !== undefined) {
            regularResults[resultIndex].tabItem = updatedItem;
          }
        }
      }

      for (const [productId, totalDeduct] of simpleDeductions) {
        await productsService.updateProductStockByDelta(productId, -totalDeduct);
      }
      for (const [ingredientId, totalDeduct] of ingredientDeductions) {
        await ingredientsService.updateIngredientStockByDelta(ingredientId, -totalDeduct);
      }

      for (let i = 0; i < regularEntries.length; i++) {
        const grouped = regularEntries[i];
        const result = regularResults[i];
        if (!result.tabItem || !isKitchenProduct(grouped.product)) continue;

        const kitchenQuantity = result.isNewItem
          ? result.tabItem.quantity
          : result.tabItem.quantity - (result.previousQuantity ?? 0);

        if (kitchenQuantity > 0) {
          kitchenTicketItems.push({
            tabItemId: result.tabItem.id,
            productId: grouped.product.id,
            productName: grouped.product.name,
            quantity: kitchenQuantity,
            notes: composeKitchenNotes(result.tabItem.notes, grouped.modifiers),
          });
        }
      }

      if (createKitchenTickets && kitchenTicketItems.length > 0) {
        await kitchenTicketsService.createTicketForTabItems(tabId, kitchenTicketItems);
      }

      return [...promoResults, ...regularResults];
    } catch (error) {
      console.error('Error adding products to tab in bulk:', error);
      throw error;
    }
  },
};
